import { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { useNavigate } from "react-router-dom";
import { api, inr, num, fmtDate, formatApiError } from "@/lib/api";
import { DatePicker } from "@/components/DatePicker";
import PageHeader from "@/components/PageHeader";
import { Paged } from "@/components/Pager";
import { StatusPill } from "./Quotations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Pencil, Ban, Plus, Boxes, CalendarClock, CalendarIcon, FileText, Receipt, Factory, RotateCcw, Brain, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { OrderPlanDialog } from "@/components/OrderPlanDialog";
import { ProductReadOnlyCards } from "@/components/ProductReadOnlyCards";
import { OutcomeBadges } from "@/components/OutcomeBadges";

const emptySched = { date: null, qty: "" };

export default function SalesOrders() {
  const [list, setList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sched, setSched] = useState(emptySched);
  const [poVendor, setPoVendor] = useState("");
  const [planFor, setPlanFor] = useState(null);
  const navigate = useNavigate();

  const load = () => Promise.all([
    api.get("/sales-orders").then((r) => setList(r.data)),
    api.get("/vendors").then((r) => setVendors(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const openSummary = async (o) => {
    try {
      const r = await api.get(`/consignments/summary/${o.id}`);
      setSummary({ ...r.data, sales_order_id: o.id });
      setSched(emptySched);
      setPoVendor(o.vendor_id || "");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const refreshSummary = async () => {
    const r = await api.get(`/consignments/summary/${summary.sales_order_id}`);
    setSummary({ ...r.data, sales_order_id: summary.sales_order_id });
  };
  const addSched = async () => {
    if (!sched.date) { toast.error("Pick a scheduled date"); return; }
    if (!(+sched.qty > 0)) { toast.error("Enter a scheduled quantity"); return; }
    const schedBal = (summary.total_qty || 0) - (summary.schedule || []).reduce((a, s) => a + (+s.planned_qty || 0), 0);
    if (+sched.qty > schedBal + 0.001) { toast.error(`Only ${schedBal} left to schedule (order total ${summary.total_qty})`); return; }
    try {
      await api.post(`/consignments/schedule/${summary.sales_order_id}`, {
        planned_date: sched.date.toISOString(), planned_qty: +sched.qty });
      toast.success("Schedule entry added"); setSched(emptySched); refreshSummary();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const schedCreatePo = async (s) => {
    if (!poVendor) { toast.error("Select a vendor first (top of the schedule panel)"); return; }
    try {
      const r = await api.post(`/sales-orders/schedule/${s.id}/create-po`, null, { params: { vendor_id: poVendor } });
      toast.success(`Purchase Order ${r.data.po_number} created for this batch`);
      refreshSummary(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delSched = async (sid) => {
    if (!window.confirm("Delete schedule entry?")) return;
    try { await api.delete(`/consignments/schedule/item/${sid}`); refreshSummary(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (id, status) => { await api.patch(`/sales-orders/${id}/status`, null, { params: { status } }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/sales-orders/${id}`); load(); };
  const reorder = async (o) => {
    if (!window.confirm(`Reorder ${o.number} for ${o.customer_snapshot?.company_name || "this client"}? A new lead will be created with the same products.`)) return;
    try {
      const r = await api.post(`/sales-orders/${o.id}/reorder`);
      toast.success(`Lead ${r.data.lead_number} created from ${r.data.ref}`);
      navigate(`/leads?q=${encodeURIComponent(r.data.lead_number)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelOrder = async (o) => {
    if (!window.confirm(`Cancel order ${o.number}?`)) return;
    await setStatus(o.id, "Cancelled");
    toast.success(`Order ${o.number} cancelled`);
  };
  const assignVendor = async (o, vendor_id) => {
    try {
      const params = { vendor_id };
      if (o.invoicing_type === "consignment") {
        const def = o.pending_qty ?? o.total_qty;
        const q = window.prompt(`Consignment order — quantity for this vendor PO?\n(Total: ${o.total_qty} · Pending: ${o.pending_qty})\nEach batch gets its own PO & job card.`, def);
        if (q === null) return;
        if (+q > 0) params.qty = +q;
      }
      const r = await api.patch(`/sales-orders/${o.id}/vendor`, null, { params });
      toast.success(`Purchase Order ${r.data.purchase_order_number} ${r.data.purchase_order_id ? "ready" : "created"} for ${r.data.vendor_name} — status: In Production`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setInvoicingType = async (id, invoicing_type) => {
    try {
      await api.put(`/sales-orders/${id}`, { invoicing_type });
      toast.success(invoicing_type === "consignment"
        ? "Marked as Consignment — use the indigo box icon to manage consignments & create partial invoices"
        : "Marked as Standard invoicing");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const refreshCustomer = async (o) => {
    try {
      const r = await api.post(`/sales-orders/${o.id}/refresh-customer`);
      toast.success(`${o.number}: address & details refreshed from ${r.data.company_name || "customer master"} — future invoices/labels use the updated address`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (o) => {
    setEditing(o.id);
    setForm({ _outcome: o.outcome, _number: o.number,
              po_number: o.po_number || "",
              date: o.date ? String(o.date).slice(0, 10) : "",
              delivery_date: o.delivery_date ? String(o.delivery_date).slice(0, 10) : "",
              freight: o.freight || 0, notes: o.notes || "",
              invoicing_type: o.invoicing_type || "standard",
              items: o.items?.length ? o.items.map((i) => ({ ...i })) : [] });
  };
  const saveEdit = async () => {
    try {
      const body = { po_number: form.po_number, freight: +form.freight || 0, notes: form.notes,
        invoicing_type: form.invoicing_type,
        items: form.items.map((i) => ({ ...i, quantity: +i.quantity, unit_price: +i.unit_price, gst: +i.gst })) };
      if (form.date) body.date = form.date;
      if (form.delivery_date) body.delivery_date = form.delivery_date;
      await api.put(`/sales-orders/${editing}`, body);
      toast.success("Sales order updated");
      setEditing(null); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totals = form ? form.items.reduce((acc, it) => {
    const sub = (+it.quantity || 0) * (+it.unit_price || 0);
    acc.sub += sub; acc.gst += sub * (+it.gst || 0) / 100; return acc;
  }, { sub: 0, gst: 0 }) : { sub: 0, gst: 0 };
  const soFreightGst = form ? (+form.freight || 0) * 0.18 : 0;

  const isCompleted = (o) => o.invoiced || o.status === "Delivered" || o.status === "Cancelled";
  const [soSearch, setSoSearch] = useUrlSearch();
  const soNorm = soSearch.trim().toLowerCase();
  const searchedList = soNorm
    ? list.filter((o) => [o.number, o.po_number, (o.customer_snapshot || {}).company_name]
        .some((v) => String(v || "").toLowerCase().includes(soNorm)))
    : list;
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const filteredList = outcomeFilter === "all" ? searchedList
    : searchedList.filter((o) => (o.outcome?.kinds || []).includes(outcomeFilter)
        || (outcomeFilter === "Cancelled" && o.status === "Cancelled"));
  const activeList = filteredList.filter((o) => !isCompleted(o));
  const completedList = filteredList.filter(isCompleted);
  const [soTab, setSoTab] = useState("active");
  useEffect(() => {
    if (soNorm && activeList.length === 0 && completedList.length > 0) setSoTab("completed");
  }, [soNorm, activeList.length, completedList.length]);

  const renderTable = (rows, emptyMsg) => (
    <Paged items={rows}>
      {(pageRows, pagerEl) => (
    <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Number</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Date</th>
              <th className="text-left px-4 py-2.5">Vendor / PO</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-right px-4 py-2.5 hidden lg:table-cell">Margin</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((o) => (
              <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`so-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-slate-700">
                  {o.number}
                  <div className="mt-1 font-sans">
                    <Select value={o.invoicing_type || "standard"} onValueChange={(v) => setInvoicingType(o.id, v)}>
                      <SelectTrigger className={`h-6 text-[11px] w-32 ${o.invoicing_type === "consignment" ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "text-slate-500"}`} data-testid={`so-invtype-${o.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="consignment">Consignment</SelectItem>
                      </SelectContent>
                    </Select>
                    {o.invoicing_type === "consignment" && (
                      <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`so-consign-info-${o.id}`}>{num(o.invoiced_qty)}/{num(o.total_qty)} · {o.progress_pct}%</div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">{o.customer_snapshot?.company_name}</td>
                <td className="px-4 py-3 hidden md:table-cell">{fmtDate(o.date)}</td>
                <td className="px-4 py-3">
                  {o.in_house ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 font-medium" data-testid={`so-inhouse-${o.id}`}>
                      <Factory size={11} /> In-House
                    </span>
                  ) : (
                  <Select value={o.vendor_id || ""} onValueChange={(v) => assignVendor(o, v)}>
                    <SelectTrigger className="h-7 text-xs w-40" data-testid={`so-vendor-${o.id}`}>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                      {vendors.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No vendors — add under Vendors</div>}
                    </SelectContent>
                  </Select>
                  )}
                  {o.purchase_order_number && (
                    <div className="text-[11px] font-mono text-emerald-700 mt-1" data-testid={`so-po-${o.id}`}>
                      PO: {o.purchase_order_number}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{inr(o.total)}</td>
                <td className="px-4 py-3 text-right hidden lg:table-cell whitespace-nowrap min-w-[90px]" data-testid={`so-margin-${o.id}`}>
                  {o.margin !== undefined ? (
                    <div className={o.margin >= 0 ? "text-emerald-700" : "text-rose-700"}>
                      <div className="tabular-nums font-medium">{inr(o.margin)}</div>
                      <div className="text-[11px]">{o.margin_pct}%</div>
                    </div>
                  ) : <span className="text-slate-400 text-xs">-</span>}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={o.status} />
                  {o.status === "Closed" && (
                    <div className="mt-1">
                      <span className="inline-flex items-center text-[10px] uppercase tracking-wider bg-orange-100 text-orange-700 border border-orange-200 rounded px-1.5 py-0.5 font-semibold"
                            data-testid={`so-closed-badge-${o.id}`}>
                        Closed — Material Returned
                      </span>
                    </div>
                  )}
                  <OutcomeBadges outcome={o.outcome} skip={o.status === "Closed" ? [o.status, "Returned"] : [o.status]} testId={`so-outcome-${o.id}`} />
                  {o.invoiced && (
                    <div className="mt-1">
                      <span className="inline-flex items-center text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5 font-medium"
                            title={(o.invoice_numbers || []).join(", ")} data-testid={`so-invoiced-${o.id}`}>
                        Invoiced ✓{o.invoice_numbers?.length ? ` ${o.invoice_numbers[0]}` : ""}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {o.invoicing_type === "consignment" && (
                      <button onClick={() => openSummary(o)} className="p-1.5 text-indigo-600 hover:text-indigo-800" title="Consignments" data-testid={`so-consign-${o.id}`}><Boxes size={13} /></button>
                    )}
                    <button onClick={() => setPlanFor(o)} className="p-1.5 text-[#3B82F6] hover:text-[#2563EB] relative" title="Order Brain — what needs to happen next" data-testid={`so-plan-${o.id}`}>
                      <Brain size={13} />
                      {o.intel_warnings > 0 && <span className="absolute top-0 right-0 min-w-[13px] h-[13px] px-0.5 text-[8px] font-bold bg-amber-500 text-white rounded-full flex items-center justify-center" data-testid={`so-plan-badge-${o.id}`}>{o.intel_warnings}</span>}
                    </button>
                    <button onClick={() => reorder(o)} className="p-1.5 text-indigo-600 hover:text-indigo-800" title="Reorder — create a new lead with these products" data-testid={`so-reorder-${o.id}`}><RotateCcw size={13} /></button>
                    <button onClick={() => openEdit(o)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Edit" data-testid={`so-edit-${o.id}`}><Pencil size={13} /></button>
                    {!["Cancelled"].includes(o.status) && (
                      <button onClick={() => refreshCustomer(o)} className="p-1.5 text-slate-500 hover:text-teal-600" title="Refresh address & details from customer master" data-testid={`so-refresh-cust-${o.id}`}><MapPinned size={13} /></button>
                    )}
                    {o.status !== "Cancelled" && o.status !== "Delivered" && (
                      <button onClick={() => cancelOrder(o)} className="p-1.5 text-amber-600 hover:text-amber-800" title="Cancel Order" data-testid={`so-cancel-${o.id}`}><Ban size={13} /></button>
                    )}
                    <button onClick={() => del(o.id)} className="p-1.5 text-slate-500 hover:text-rose-600" title="Delete" data-testid={`so-del-${o.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-500">{emptyMsg}</td></tr>}
          </tbody>
        </table>
        {pagerEl}
    </div>
      )}
    </Paged>
  );

  return (
    <div className="space-y-4" data-testid="orders-page">
      <PageHeader title="Sales Orders" subtitle="Confirmed orders and delivery pipeline"
        right={
          <div className="flex items-center gap-2">
            <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
              <SelectTrigger className={`h-9 w-40 bg-white ${outcomeFilter !== "all" ? "border-rose-300 text-rose-700" : ""}`} data-testid="so-outcome-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="Returned">Returned</SelectItem>
                <SelectItem value="Refunded">Refunded</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Input value={soSearch} onChange={(e) => setSoSearch(e.target.value)} placeholder="Search no., customer, PO…"
                   className="h-9 w-64 bg-white" data-testid="so-search" />
          </div>
        } />
      <Tabs value={soTab} onValueChange={setSoTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="sotab-active">Active Sales Orders ({activeList.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="sotab-completed">Completed Sales Orders ({completedList.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="active">{renderTable(activeList, "No active sales orders. Convert a quotation to create one.")}</TabsContent>
        <TabsContent value="completed">{renderTable(completedList, "No completed sales orders yet — orders appear here once invoiced, delivered or cancelled.")}</TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(null); } }}>
        <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden flex flex-col content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
          <DialogHeader><DialogTitle>Edit Sales Order</DialogTitle></DialogHeader>
          {form && (
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pl-1 pr-4 space-y-3" data-testid="so-dialog-body">
              {form._outcome?.events?.length > 0 && (
                <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-1" data-testid="so-outcome-alert">
                  <p className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Order History Alert — {form._number}</p>
                  {form._outcome.events.map((e, i) => (
                    <div key={i} className="text-xs text-orange-800 flex gap-1.5">
                      <span className="font-semibold whitespace-nowrap">{e.kind}:</span>
                      <span>{e.label}{e.date ? ` — ${fmtDate(e.date)}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Customer PO No. <span className="normal-case text-slate-400">(from pro forma)</span></Label>
                  <Input value={form.po_number} disabled className="mt-1.5 bg-slate-50 text-slate-700 disabled:opacity-100" data-testid="so-edit-po" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Order Date <span className="normal-case text-slate-400">(from pro forma)</span></Label>
                  <Input value={form.date ? fmtDate(form.date) : "-"} disabled className="mt-1.5 bg-slate-50 text-slate-700 disabled:opacity-100" data-testid="so-edit-date" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Delivery Date</Label>
                  <DatePicker value={form.delivery_date} onChange={(v) => setForm({ ...form, delivery_date: v })} className="mt-1.5" testId="so-edit-delivery" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Freight (INR)</Label>
                  <Input type="number" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Invoicing</Label>
                  <Select value={form.invoicing_type} onValueChange={(v) => setForm({ ...form, invoicing_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="so-invoicing-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (Single Invoice)</SelectItem>
                      <SelectItem value="consignment">Consignment (Multiple Dispatches)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ProductReadOnlyCards items={form.items} note="from pro forma — read-only" testPrefix="so-item" />

              <div className="flex justify-end">
                <div className="w-full max-w-xs space-y-1 text-sm text-slate-600">
                  <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{inr(totals.sub)}</span></div>
                  <div className="flex justify-between"><span>GST{+form.freight > 0 ? " (incl. 18% on freight)" : ""}</span><span className="tabular-nums">{inr(totals.gst + soFreightGst)}</span></div>
                  {+form.freight > 0 && <div className="flex justify-between"><span>Freight</span><span className="tabular-nums">{inr(+form.freight)}</span></div>}
                  <div className="flex justify-between font-bold text-slate-900 border-t border-slate-300 pt-1"><span>Total</span><span className="tabular-nums">{inr(totals.sub + totals.gst + soFreightGst + (+form.freight || 0))}</span></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="so-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!summary} onOpenChange={(v) => !v && setSummary(null)}>
        <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden flex flex-col content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
          <DialogHeader>
            <DialogTitle>Consignments — {summary?.order_number}{summary?.po_number ? ` (Cust. PO: ${summary.po_number})` : ""}</DialogTitle>
          </DialogHeader>
          {summary && (
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pl-1 pr-4 space-y-4" data-testid="cons-dialog-body">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                {[["Total PO Qty", num(summary.total_qty), "cs-total"],
                  ["Dispatched", num(summary.invoiced_qty), "cs-dispatched"],
                  ["Pending", num(summary.pending_qty), "cs-pending"],
                  ["Consignments Done", summary.consignments_count, "cs-count"],
                  ["Scheduled Remaining", summary.schedule?.length ? summary.schedule_remaining : "—", "cs-remaining"]].map(([l, v, tid]) => (
                  <div key={l} className="border border-slate-200 rounded-md p-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
                    <p className="text-lg font-bold tabular-nums" data-testid={tid}>{v}</p>
                  </div>
                ))}
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Progress</p>
                  <p className="text-lg font-bold tabular-nums" data-testid="cs-progress">{summary.progress_pct}%</p>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className={`h-full rounded-full ${summary.progress_pct >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, summary.progress_pct)}%` }} />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className={`text-[11px] uppercase tracking-wider rounded px-2 py-1 ${
                  summary.consignment_status === "Fully Dispatched" ? "bg-emerald-100 text-emerald-700" :
                  summary.consignment_status === "Partially Dispatched" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                }`}>{summary.consignment_status}</span>
                {summary.pending_qty > 0 && (
                  <Button onClick={() => navigate(`/invoices?so=${summary.sales_order_id}`)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="cs-new-invoice">
                    <Receipt size={14} className="mr-1" /> New Invoice
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <div className="space-y-4">
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <div className="px-3 py-2 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">Item-wise Balance</div>
                    <table className="w-full text-sm" data-testid="cs-items">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr><th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Ordered</th><th className="text-right px-3 py-2">Invoiced</th><th className="text-right px-3 py-2">Remaining</th></tr>
                      </thead>
                      <tbody>
                        {summary.per_item.map((p, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-xs">{p.description}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{num(p.ordered)} {p.unit}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{num(p.invoiced)}</td>
                            <td className={`px-3 py-2 text-right tabular-nums font-medium ${p.remaining > 0 ? "text-amber-700" : "text-emerald-700"}`}>{num(p.remaining)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <div className="px-3 py-2 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">Consignments</div>
                    <table className="w-full text-sm" data-testid="cs-consignments">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                        <tr><th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">Invoice</th><th className="text-right px-3 py-2">Qty</th><th className="text-left px-3 py-2">LR</th><th className="text-left px-3 py-2">Date</th></tr>
                      </thead>
                      <tbody>
                        {summary.consignments.map((c) => (
                          <tr key={c.id} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-mono text-xs">{c.seq}</td>
                            <td className="px-3 py-2 font-mono text-xs">{c.invoice_number}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{num(c.quantity)}</td>
                            <td className="px-3 py-2 font-mono text-xs">{c.lr_number || "-"}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(c.created_at)}</td>
                          </tr>
                        ))}
                        {summary.consignments.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">No consignments yet — click "New Invoice" to create one from the Invoices page.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-3 py-2 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between gap-2 flex-wrap">
                    <span className="flex items-center gap-1.5"><CalendarClock size={13} /> Dispatch Schedule</span>
                    <span className="normal-case font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="cs-balance-qty">Balance Qty: {num(summary.pending_qty)}</span>
                  </div>
                  <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Vendor for POs:</span>
                    <Select value={poVendor} onValueChange={setPoVendor}>
                      <SelectTrigger className="h-8 text-xs w-56" data-testid="cs-po-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                      <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <table className="w-full text-sm" data-testid="cs-schedule">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                      <tr><th className="text-left px-3 py-2">Scheduled Date</th><th className="text-right px-3 py-2">Scheduled Qty</th><th className="text-left px-3 py-2">PO</th><th className="text-left px-3 py-2">Invoice</th><th className="text-right px-3 py-2">Actions</th></tr>
                    </thead>
                    <tbody>
                      {summary.schedule.map((s) => (
                        <tr key={s.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-xs">{s.planned_date ? fmtDate(s.planned_date) : "-"}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{num(s.planned_qty)}</td>
                          <td className="px-3 py-2 font-mono text-xs">{s.po_number || <span className="text-slate-400 font-sans">—</span>}</td>
                          <td className="px-3 py-2 font-mono text-xs">{s.invoice_number || <span className="text-slate-400 font-sans">—</span>}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-1.5">
                              {!s.po_number && (
                                <button onClick={() => schedCreatePo(s)} className="text-[11px] px-2 py-1 border border-blue-200 text-blue-700 rounded inline-flex items-center gap-1 hover:bg-blue-50" data-testid={`cs-sched-po-${s.id}`}><FileText size={11} /> Vendor PO</button>
                              )}
                              {!s.invoice_number && !s.po_number && (
                                <button onClick={() => delSched(s.id)} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`cs-sched-del-${s.id}`}><Trash2 size={13} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {summary.schedule.length === 0 && <tr><td colSpan={5} className="text-center py-5 text-sm text-slate-500">No schedule yet — add batches below (e.g. 100m on the 1st of each month).</td></tr>}
                    </tbody>
                  </table>
                  <div className="border-t border-slate-200 p-3">
                    <div className="flex items-end gap-2 flex-wrap">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Scheduled Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="mt-1 h-9 w-44 justify-start text-left font-normal text-sm" data-testid="cs-sched-date">
                              <CalendarIcon size={13} className="mr-2 text-slate-400" />
                              {sched.date ? sched.date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : <span className="text-slate-400">Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={sched.date} onSelect={(d) => setSched({ ...sched, date: d })} initialFocus />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Scheduled Qty</Label>
                        <Input type="number" value={sched.qty} onChange={(e) => setSched({ ...sched, qty: e.target.value })}
                               placeholder={`max ${summary.pending_qty}`} className="mt-1 h-9 w-32" data-testid="cs-sched-qty" />
                      </div>
                      <Button size="sm" onClick={addSched} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-9" data-testid="cs-sched-add"><Plus size={13} className="mr-1" /> Add</Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <OrderPlanDialog order={planFor} onClose={() => setPlanFor(null)} />
    </div>
  );
}
